/**
 * Character sheet view-model (pure, DOM-free, unit-tested): final stats from compileCharacter,
 * stat deltas for the equip comparison, stat explanations, equip candidates and the active
 * gem bonuses / legendary / trinket effects of a compiled character.
 */
import type { ClassDef, ClassId, StatMod } from '@keystone/sim';
import type { ClassTransform, CompiledCharacter, EquippedGear, GemColor, GemPowerBonus, Item, ItemSlot, OpError, Profile } from '@keystone/progression';
import {
  CLASS_STAT_PROFILES,
  GEM_BONUSES,
  GEM_COLORS,
  POWER_LATE_FROM,
  POWER_PER_ILVL,
  POWER_PER_ILVL_LATE,
  RATING_PER_PERCENT,
  SPIRIT_REFUND_SHARE,
  compileCharacter,
  withSpec,
  equippedGear,
  gemProgress,
  itemScore,
  legendary,
  roleOf,
  sanitizeTalents,
  trinketEffect,
  unequippedItems,
} from '@keystone/progression';

// ---------------------------------------------------------------------------
// Stats
// ---------------------------------------------------------------------------

export type SheetStatKey = 'ilvl' | 'power' | 'hp' | 'haste' | 'crit' | 'expertise' | 'spirit';

/** The numbers the sheet shows. Secondaries are fractions (0.12 = 12%). */
export interface SheetStats {
  ilvl: number;
  power: number;
  powerMult: number;
  hp: number;
  haste: number;
  crit: number;
  expertise: number;
  spirit: number;
}

/** Final stats of a compiled character (base class stats + gear). */
export function sheetStats(c: CompiledCharacter): SheetStats {
  return {
    ilvl: c.sheet.avgIlvl,
    power: c.stats.power,
    powerMult: c.sheet.powerMult,
    hp: c.maxHp,
    haste: c.stats.haste,
    crit: c.stats.crit,
    // Expertise and spirit are gear-only (the class base has none of either).
    expertise: c.sheet.secondaries.expertise,
    spirit: c.sheet.secondaries.spirit,
  };
}

export const STAT_LABELS: Readonly<Record<SheetStatKey, string>> = {
  ilvl: 'Item Level',
  power: 'Power',
  hp: 'Health',
  haste: 'Haste',
  crit: 'Critical Strike',
  expertise: 'Expertise',
  spirit: 'Spirit',
};

const PCT_STATS: ReadonlySet<SheetStatKey> = new Set(['haste', 'crit', 'expertise', 'spirit']);
export const isPctStat = (k: SheetStatKey): boolean => PCT_STATS.has(k);

/** "12.3%" for fractions, "1,234" for integers, "212.5" for item level. */
export function formatStat(key: SheetStatKey, v: number): string {
  if (isPctStat(key)) return `${(v * 100).toFixed(1)}%`;
  if (key === 'ilvl') return v.toFixed(1);
  return groupInt(v);
}

function groupInt(n: number): string {
  const s = String(Math.round(Math.abs(n))).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return n < 0 ? `-${s}` : s;
}

export type DeltaTone = 'up' | 'down' | 'same';

export interface StatDelta {
  key: SheetStatKey;
  label: string;
  before: number;
  after: number;
  delta: number;
  tone: DeltaTone;
  /** "+1.2%", "−340", "±0". */
  text: string;
}

/** Below these a change is shown as "same" (rounding noise). */
const EPS: Readonly<Record<SheetStatKey, number>> = { ilvl: 0.05, power: 0.5, hp: 0.5, haste: 0.0005, crit: 0.0005, expertise: 0.0005, spirit: 0.0005 };

export const DELTA_ORDER: readonly SheetStatKey[] = ['ilvl', 'power', 'hp', 'haste', 'crit', 'expertise', 'spirit'];

/**
 * Per-stat change from `before` to `after` (every stat is "more is better", so a rise is
 * green/up and a drop red/down). `onlyChanged` drops the "same" rows.
 */
export function statDeltas(before: SheetStats, after: SheetStats, opts: { onlyChanged?: boolean } = {}): StatDelta[] {
  const out: StatDelta[] = [];
  for (const key of DELTA_ORDER) {
    const b = before[key];
    const a = after[key];
    const d = a - b;
    const tone: DeltaTone = Math.abs(d) < EPS[key] ? 'same' : d > 0 ? 'up' : 'down';
    if (opts.onlyChanged && tone === 'same') continue;
    out.push({ key, label: STAT_LABELS[key], before: b, after: a, delta: d, tone, text: deltaText(key, d, tone) });
  }
  return out;
}

function deltaText(key: SheetStatKey, d: number, tone: DeltaTone): string {
  if (tone === 'same') return '±0';
  const sign = d > 0 ? '+' : '−';
  const abs = Math.abs(d);
  if (isPctStat(key)) return `${sign}${(abs * 100).toFixed(1)}%`;
  if (key === 'ilvl') return `${sign}${abs.toFixed(1)}`;
  return `${sign}${groupInt(abs)}`;
}

// ---------------------------------------------------------------------------
// Compile helpers
// ---------------------------------------------------------------------------

/** Gear with `item` in its slot (the "what if I equip this" preview). */
export function gearWith(gear: EquippedGear, item: Item): EquippedGear {
  return { ...gear, [item.slot]: item };
}

/** Gear with a slot emptied. */
export function gearWithout(gear: EquippedGear, slot: ItemSlot): EquippedGear {
  const g = { ...gear };
  delete g[slot];
  return g;
}

/**
 * Compile a profile character, optionally with different gear. Same talents as
 * compileProfileCharacter (the active loadout, sanitised to the hero's points).
 */
export function compileFor(profile: Profile, classDef: ClassDef, gear: EquippedGear = equippedGear(profile, classDef.id)): CompiledCharacter {
  const ch = profile.characters[classDef.id];
  const lo = ch?.loadouts.find((l) => l.id === ch.activeLoadoutId);
  // +specs: the sheet shows the specialisation's stats (a dps Warrior has melee health).
  return compileCharacter(withSpec(profile, classDef), gear, sanitizeTalents(classDef.id, lo?.nodes ?? [], ch?.hero.talentPoints ?? 0));
}

/** Stat deltas of equipping `item` on `classDef.id` (null when compiling fails). */
export function equipPreview(profile: Profile, classDef: ClassDef, item: Item): StatDelta[] | null {
  try {
    const gear = equippedGear(profile, classDef.id);
    const before = sheetStats(compileFor(profile, classDef, gear));
    const after = sheetStats(compileFor(profile, classDef, gearWith(gear, item)));
    return statDeltas(before, after);
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Explanations (stat tooltips)
// ---------------------------------------------------------------------------

const MOD_LABEL: Readonly<Record<string, string>> = {
  healingDonePct: 'healing done',
  damageDonePct: 'damage done',
  damageTakenPct: 'damage taken',
  hastePct: 'haste',
  critPct: 'critical strike chance',
  cooldownRatePct: 'cooldown recovery',
};

/** "+12.0% healing done by Bulwark Strike" for an expertise mod scaled by `fraction`. */
export function describeScaledMod(mod: StatMod, fraction: number, abilityName: (id: string) => string): string {
  const v = mod.value * fraction * 100;
  const what = MOD_LABEL[mod.stat] ?? mod.stat.replace(/Pct$/, '').replace(/([A-Z])/g, ' $1').toLowerCase();
  const sign = v >= 0 ? '+' : '−';
  let scope = '';
  if (mod.abilityIds?.length) scope = ` by ${mod.abilityIds.map(abilityName).join(' and ')}`;
  else if (mod.school) scope = ` (${mod.school} only)`;
  return `${sign}${Math.abs(v).toFixed(1)}% ${what}${scope}`;
}

export interface StatExplain {
  title: string;
  value: string;
  lines: string[];
}

/** Tooltip text for one sheet stat. */
export function explainStat(key: SheetStatKey, s: SheetStats, classId: ClassId, abilityName: (id: string) => string = (id) => id): StatExplain {
  const pct = (v: number): string => `${(v * 100).toFixed(1)}%`;
  const rating = (v: number): string => `${Math.round(v * 100 * RATING_PER_PERCENT)} rating`;
  switch (key) {
    case 'ilvl':
      return {
        title: STAT_LABELS.ilvl,
        value: s.ilvl.toFixed(1),
        lines: [
          'Average item level of your 8 slots (empty slots count as 180).',
          `Each item level above 200 raises Power and Health by ${((POWER_PER_ILVL - 1) * 100).toFixed(1)}%, and each above ${POWER_LATE_FROM} by ${((POWER_PER_ILVL_LATE - 1) * 100).toFixed(2).replace(/0$/, '')}% (compounding).`,
          `Current multiplier: ×${s.powerMult.toFixed(3)}.`,
        ],
      };
    case 'power':
      return {
        title: STAT_LABELS.power,
        value: formatStat('power', s.power),
        lines: ['Scales every damage, healing and shield your abilities do.', `Comes from item level: ×${s.powerMult.toFixed(3)} over a fresh ilvl-200 hero.`],
      };
    case 'hp':
      return {
        title: STAT_LABELS.hp,
        value: formatStat('hp', s.hp),
        lines: ['Maximum health. Scales with item level the same way Power does.'],
      };
    case 'haste':
      return {
        title: STAT_LABELS.haste,
        value: pct(s.haste),
        lines: [`Global cooldown, cast times, channels, melee swings and hasted periodic ticks are ${pct(s.haste)} faster (divided by ${(1 + s.haste).toFixed(3)}).`],
      };
    case 'crit':
      return {
        title: STAT_LABELS.crit,
        value: pct(s.crit),
        lines: [`${pct(s.crit)} chance for damage and healing to critically strike for double the amount.`],
      };
    case 'expertise': {
      const mods = CLASS_STAT_PROFILES[classId]?.expertise ?? [];
      return {
        title: STAT_LABELS.expertise,
        value: pct(s.expertise),
        lines: [
          'Class-specific amplifier (like mastery).',
          ...(mods.length ? mods.map((m) => `${describeScaledMod(m, s.expertise, abilityName)}.`) : ['No expertise effect for this class.']),
          `${rating(s.expertise)} from gear (${RATING_PER_PERCENT} rating = 1%).`,
        ],
      };
    }
    case 'spirit':
      return {
        title: STAT_LABELS.spirit,
        value: pct(s.spirit),
        lines: [
          `Cooldowns recover ${pct(s.spirit)} faster.`,
          `${pct(Math.min(1, s.spirit))} chance for a resource-costing cast to refund ${Math.round(SPIRIT_REFUND_SHARE * 100)}% of its cost.`,
          `${rating(s.spirit)} from gear (${RATING_PER_PERCENT} rating = 1%).`,
        ],
      };
  }
}

// ---------------------------------------------------------------------------
// Effects (gem bonuses, legendaries, trinkets)
// ---------------------------------------------------------------------------

export interface EffectLine {
  kind: 'gem' | 'legendary' | 'trinket';
  name: string;
  description: string;
  color?: GemColor;
  /** The transforms it applies (for the ability tooltip's per-button changes). */
  effect?: readonly ClassTransform[];
}

/** Active gem bonuses, legendary and trinket effects, in that order. */
export function activeEffects(c: CompiledCharacter): EffectLine[] {
  const out: EffectLine[] = [];
  for (const id of c.sheet.gemBonuses) {
    const b: GemPowerBonus | undefined = GEM_BONUSES.find((x) => x.id === id);
    if (b) out.push({ kind: 'gem', name: b.name, description: b.description, color: b.color, effect: b.effect });
  }
  for (const id of c.sheet.legendaries) {
    const l = legendary(id);
    if (l) out.push({ kind: 'legendary', name: l.name, description: l.description, effect: l.effect });
  }
  for (const id of c.sheet.trinkets) {
    const t = trinketEffect(id);
    if (t) out.push({ kind: 'trinket', name: t.name, description: t.description, effect: t.effect });
  }
  return out;
}

export interface GemPowerRow {
  color: GemColor;
  power: number;
  rank: number;
  next: number | null;
  nextBonus: GemPowerBonus | null;
}

export function gemPowerRows(c: CompiledCharacter): GemPowerRow[] {
  const prog = gemProgress(c.sheet.gemPower, c.classId);
  return GEM_COLORS.map((color) => ({ color, power: c.sheet.gemPower[color], ...prog[color] }));
}

// ---------------------------------------------------------------------------
// Equip candidates
// ---------------------------------------------------------------------------

export interface EquipCandidate {
  item: Item;
  score: number;
  /** Why it cannot be equipped on this class (shown greyed out), or null. */
  blocked: OpError | null;
}

/**
 * Bag items (not worn by anyone) for `slot`, best item score first. Legendaries for another
 * class are listed but blocked.
 */
export function slotCandidates(profile: Profile, classId: ClassId, slot: ItemSlot): EquipCandidate[] {
  return unequippedItems(profile)
    .filter((i) => i.slot === slot)
    .map((item) => {
      const blocked: OpError | null = item.legendaryId && legendary(item.legendaryId)?.classId !== classId ? 'wrongClass' : null;
      return { item, score: itemScore(item, classId), blocked };
    })
    .sort((a, b) => Number(!!a.blocked) - Number(!!b.blocked) || b.score - a.score || a.item.id.localeCompare(b.item.id));
}

/** Player-facing text for a gear-op failure. */
export function opErrorMessage(e: OpError): string {
  const m: Partial<Record<OpError, string>> = {
    unknownItem: 'That item no longer exists.',
    unknownClass: 'Unknown hero.',
    wrongClass: 'This legendary is for another class.',
    legendaryLimit: 'Only one legendary can be equipped at a time.',
    equippedElsewhere: 'Another hero is wearing that item.',
    equipped: 'Unequip it first.',
    locked: 'Not unlocked at your rank yet.',
    notEnoughShards: 'Not enough Shards.',
    notEnoughGold: 'Not enough gold.',
  };
  return m[e] ?? `Could not do that (${e}).`;
}

/** Role label for a class ("tank"). */
export const roleFor = roleOf;
